import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import SectionEyebrow from "@/components/common/SectionEyebrow";
import { publicApi } from "@/lib/api";
import { ArrowRight, Globe2, Microscope, Sparkles, Atom, Brain, Quote, Compass } from "lucide-react";

const ICONS = [Globe2, Microscope, Compass, Atom, Brain, Sparkles];

export default function Manifesto() {
  const [page, setPage] = useState(null);
  useEffect(() => {
    publicApi.page("manifesto").then(setPage).catch(() => {});
  }, []);
  const p = page || {};

  return (
    <PublicLayout title="Manifesto">
      {/* Dark cinematic hero */}
      <section className="relative bsca-dark bsca-cosmic bsca-grain -mt-16 md:-mt-20 lg:-mt-[88px] pt-36 md:pt-48 pb-28 md:pb-36 overflow-hidden">
        <div className="bsca-field-lines" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 lg:px-14 bsca-fade-up">
          <SectionEyebrow dark>{p.hero?.eyebrow}</SectionEyebrow>
          <h1 className="font-serif text-white text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02]">
            {p.hero?.headline}
          </h1>
          <p className="mt-7 text-white/75 text-lg max-w-2xl">{p.hero?.subheadline}</p>
        </div>
      </section>

      {/* Preamble */}
      <section className="bg-[#F8F6F1] py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <SectionEyebrow>{p.preamble?.title}</SectionEyebrow>
          <p className="font-serif text-2xl md:text-3xl leading-snug tracking-tight text-[#1f2937]">
            {p.preamble?.body}
          </p>
        </div>
      </section>

      {/* Vision + Mission */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <SectionEyebrow>{p.vision?.title}</SectionEyebrow>
            <p className="font-serif text-2xl tracking-tight leading-snug">{p.vision?.body}</p>
          </div>
          <div className="md:col-span-7 border-l border-black/10 md:pl-12">
            <SectionEyebrow>{p.mission?.title}</SectionEyebrow>
            <ul className="space-y-4">
              {(p.mission?.items || []).map((it, i) => (
                <li key={it} className="flex items-start gap-4">
                  <span className="bsca-eyebrow text-[#D4AF37] mt-1.5">0{i + 1}</span>
                  <span className="text-[#1f2937] leading-relaxed">{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Six Principles */}
      <section className="bsca-dark bsca-cosmic bsca-grain py-28 relative overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          <div className="max-w-2xl">
            <SectionEyebrow dark>The Six Principles</SectionEyebrow>
            <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tight">
              How we work. <span className="text-[#D4AF37]">How we know.</span>
            </h2>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 bsca-stagger">
            {(p.principles || []).map((pr, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <div key={pr.title} data-testid={`manifesto-principle-${i}`} className="bg-[#050B14] p-10 hover:bg-[#0A1120] transition-colors">
                  <Icon className="h-7 w-7 text-[#D4AF37]" strokeWidth={1.3} />
                  <h3 className="mt-6 font-serif text-2xl text-white">{pr.title}</h3>
                  <p className="mt-3 text-white/65 leading-relaxed">{pr.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Membership + Programs */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-2 gap-12">
          <div>
            <SectionEyebrow>{p.membership?.title}</SectionEyebrow>
            <p className="text-lg leading-relaxed text-[#1f2937]">{p.membership?.body}</p>
            <Link to="/engage#join" data-testid="btn-manifesto-join" className="mt-8 inline-flex items-center bg-[#050B14] text-white px-7 py-3.5 rounded-sm bsca-lift">Join BSCA <ArrowRight className="h-4 w-4 ml-2" /></Link>
          </div>
          <div>
            <SectionEyebrow>{p.programs_summary?.title}</SectionEyebrow>
            <p className="text-lg leading-relaxed text-[#1f2937]">{p.programs_summary?.body}</p>
            <Link to="/programs" data-testid="btn-manifesto-programs" className="mt-8 inline-flex items-center border border-[#050B14] text-[#050B14] px-7 py-3.5 rounded-sm bsca-lift hover:bg-[#050B14] hover:text-white">View programs <ArrowRight className="h-4 w-4 ml-2" /></Link>
          </div>
        </div>
      </section>

      {/* Call to Action band */}
      <section className="bsca-dark bsca-cosmic bsca-grain py-28 relative overflow-hidden">
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Quote className="h-7 w-7 text-[#D4AF37] mx-auto" strokeWidth={1.2} />
          <h2 className="mt-6 font-serif text-4xl md:text-5xl text-white tracking-tight leading-tight">
            {p.cta?.title}
          </h2>
          <p className="mt-5 text-white/70">{p.cta?.body}</p>
          <Link to={p.cta?.href || "/engage#join"} data-testid="btn-manifesto-cta" className="mt-10 inline-flex items-center bg-[#D4AF37] text-[#050B14] font-medium px-8 py-4 rounded-sm bsca-lift">
            {p.cta?.label || "Join the Alliance"} <ArrowRight className="h-4 w-4 ml-2" />
          </Link>
        </div>
      </section>

      {/* Charter summary */}
      <section className="bg-[#F8F6F1] py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <SectionEyebrow>Charter Summary</SectionEyebrow>
          <p className="text-[#4B5563] leading-relaxed">{p.charter}</p>
        </div>
      </section>
    </PublicLayout>
  );
}
